import React, { useState, useEffect } from "react";
import { Card, Switch, Typography, message, Spin } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import Theme from "../../theme/Theme";

const { Title, Text } = Typography;

function Customization() {
  const [loading, setLoading] = useState(false);
  const [widgets, setWidgets] = useState({
    stockMarket: true,
    googleNews: true,
    attentionRequired: true,
    needsAttentionToday: true,
    employeePerformanceSummary: true,
    quickActions: true,
    recentActivities: true,
  });

  // Load saved preferences from localStorage
  useEffect(() => {
    const savedPreferences = localStorage.getItem("widgetPreferences");
    if (savedPreferences) {
      try {
        setWidgets(JSON.parse(savedPreferences));
      } catch (error) {
        console.error("Error loading widget preferences:", error);
      }
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = (newWidgets) => {
    try {
      localStorage.setItem("widgetPreferences", JSON.stringify(newWidgets));
      message.success("Widget preferences saved");
    } catch (error) {
      console.error("Error saving widget preferences:", error);
      message.error("Failed to save preferences");
    }
  };

  const handleToggle = (widgetKey) => {
    const newWidgets = {
      ...widgets,
      [widgetKey]: !widgets[widgetKey],
    };
    setWidgets(newWidgets);
    savePreferences(newWidgets);
  };

  const widgetList = [
    {
      key: "attentionRequired",
      title: "Attention Required",
      description: "Shows plans that need immediate attention with performance alerts",
    },
    {
      key: "needsAttentionToday",
      title: "Needs Your Attention Today",
      description: "Time-sensitive plans, tasks and KPIs due soon or overdue",
    },
    {
      key: "employeePerformanceSummary",
      title: "Employee Performance Summary",
      description: "Ranked list of employees by their leaf-plan performance for the current cycle",
    },
    {
      key: "quickActions",
      title: "Quick Actions",
      description: "Quick access buttons for common tasks like updating targets and approvals",
    },
    {
      key: "recentActivities",
      title: "Recent Activities",
      description: "Displays recent notifications and activities from your team",
    },
    {
      key: "googleNews",
      title: "Google News",
      description: "Shows latest news articles relevant to your organization",
    },
    {
      key: "stockMarket",
      title: "CSE Stock Market",
      description: "Displays real-time stock market data from Colombo Stock Exchange",
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <Title 
          level={3} 
          style={{ 
            color: Theme().textPrimary,
            fontFamily: 'Inter, sans-serif',
            marginBottom: "8px",
          }}
        >
          Dashboard Customization
        </Title>
        <Text 
          style={{ 
            color: Theme().textPrimary,
            opacity: 0.7,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Enable or disable widgets on your home dashboard
        </Text>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {widgetList.map((widget) => (
            <Card
              key={widget.key}
              style={{
                borderRadius: "8px",
                border: `1px solid ${Theme().navBorder}`,
              }}
              bodyStyle={{ padding: "16px" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1, paddingRight: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                    {widgets[widget.key] ? (
                      <EyeOutlined style={{ color: Theme().primary, fontSize: "16px", marginRight: "10px" }} />
                    ) : (
                      <EyeInvisibleOutlined style={{ color: "#999", fontSize: "16px", marginRight: "10px" }} />
                    )}
                    <Title 
                      level={5} 
                      style={{ 
                        margin: 0,
                        color: Theme().textPrimary,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: "15px",
                      }}
                    >
                      {widget.title}
                    </Title>
                  </div>
                  <Text 
                    style={{ 
                      color: Theme().textPrimary,
                      opacity: 0.7,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: "13px",
                      paddingLeft: "26px",
                      display: "block",
                    }}
                  >
                    {widget.description}
                  </Text>
                </div>
                <Switch
                  checked={widgets[widget.key]}
                  onChange={() => handleToggle(widget.key)}
                  style={{
                    backgroundColor: widgets[widget.key] ? Theme().primary : "#d9d9d9",
                  }}
                />
              </div>
            </Card>
          ))}
        </div>
      )}

      <div 
        style={{ 
          marginTop: "32px",
          padding: "16px",
          background: "rgba(255, 227, 219, 0.3)",
          borderRadius: "8px",
          border: `1px solid ${Theme().navBorder}`,
        }}
      >
        <Text 
          style={{ 
            color: Theme().textPrimary,
            fontFamily: 'Inter, sans-serif',
            fontSize: "13px",
          }}
        >
          <strong>Note:</strong> Changes are saved automatically and will take effect immediately on your home dashboard.
        </Text>
      </div>
    </div>
  );
}

export default Customization;
